import React, { useState } from 'react';
import { Button, DialogActions, DialogContent, Stack } from '@mui/material';
import Modal from 'components/_layouts/Modal';
import { useParams } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import FileUploader, { FileInfo } from 'components/_atoms/FileUploader';
import useManageAttachment from '../../../_data_sources/useManageAttachment';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';

interface AttachmentAddDialogProps {
  open: boolean;
  onClose: (didAdd: boolean, attachmentId?: string) => void;
}

export function AttachmentAddDialog({ open, onClose }: AttachmentAddDialogProps) {
  const { id: manualId } = useParams();
  const { getLock, removeLock } = useManageManual();
  const { createAttachment, getAttachment, base64ToBlob, uploadFile } = useManageAttachment();
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (info: FileInfo) => {
    setFileInfo(info);
  };

  const handleConfirm = async () => {
    if (!fileInfo || !manualId) return;

    setIsUploading(true);

    try {
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => resolve(),
          (err) => reject(err)
        );
      });

      let uploadUrl: string | null = null;
      let attachmentId: string | null = null;
      await new Promise<void>((resolve, reject) => {
        createAttachment(
          {
            manualId,
            file: new File([fileInfo.data], fileInfo.name, { type: fileInfo.type }),
            name: fileInfo.name,
            size: fileInfo.size,
            globalAttachmentId: '',
            jurisdictionAttachmentId: '',
          },
          (url, id) => {
            uploadUrl = url;
            attachmentId = id;
            resolve();
          },
          (err) => reject(err)
        );
      });

      if (!uploadUrl || !attachmentId) {
        throw new Error('Presigned URL or attachment ID is missing');
      }
      const blob = base64ToBlob(fileInfo.data, fileInfo.type);
      const file = new File([blob], fileInfo.name, { type: fileInfo.type });
      await uploadFile(uploadUrl, file);
      onClose(true, attachmentId);
    } catch (err) {
      console.error('Error in lock/create/upload flow:', err);
      onClose(false);
    } finally {
      if (manualId) {
        await new Promise<void>((resolve, reject) => {
          removeLock(
            manualId,
            () => resolve(),
            (err) => reject(err)
          );
        });
      }
      setIsUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
      title={t('draftManuals.attachmentLibrary.addDialogTitle')}
      size='sm'
    >
      <DialogContent>
        <Stack spacing={2}>
          <FileUploader
            onChange={handleFileChange}
            onError={(errors) => {
              console.error('File upload errors:', errors);
            }}
            placeholder={t('draftManuals.attachmentLibrary.fileUploaderPlaceholder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color='neutral'
          onClick={() => onClose(false)}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant='contained'
          disabled={!fileInfo || isUploading}
          onClick={handleConfirm}
        >
          {isUploading ? t('draftManuals.attachmentLibrary.uploading') : t('draftManuals.attachmentLibrary.upload')}
        </Button>
      </DialogActions>
    </Modal>
  );
}
